import { useState } from "react";
import { Tabs, Table, Tag, Spin, Tooltip } from "antd";
import dayjs from "dayjs";
import Header from "../_shared/components/Header";
import { useGetQueuesQuery, useGetQueueQuery } from "../../../redux/api/scheduling";
import { formatDate, formatShortDateTime } from "../_shared/utils/dateUtils";

const TIMELINESS_COLORS: Record<string, string> = { on_track: "green", at_risk: "orange", late: "red" };

function splitTabName(name: string): [string, string] | [string] {
  const spaces: number[] = [];
  for (let i = 0; i < name.length; i++) {
    if (name[i] === " ") spaces.push(i);
  }
  if (spaces.length === 0) return [name];
  const mid = name.length / 2;
  let best = spaces[0];
  for (const sp of spaces) {
    if (Math.abs(sp - mid) < Math.abs(best - mid)) best = sp;
  }
  return [name.slice(0, best), name.slice(best + 1)];
}

function TabLabel({ name }: { name: string }) {
  const parts = splitTabName(name);
  if (parts.length === 1) {
    return <span className="inline-block text-center leading-tight">{parts[0]}</span>;
  }
  return (
    <span className="inline-block text-center leading-tight">
      {parts[0]}<br />{parts[1]}
    </span>
  );
}

function QueueTab({ taskId, taskCode, taskName, queueCount }: {
  taskId: number;
  taskCode: string;
  taskName: string;
  queueCount: number;
}) {
  const { data, isLoading } = useGetQueueQuery(taskId);
  const items = data || [];

  const columns = [
    { title: "Order", dataIndex: "order_name", key: "order_name" },
    { title: "Part", dataIndex: "part_name", key: "part_name" },
    {
      title: "Batch",
      dataIndex: "batch_index",
      key: "batch_index",
      width: 70,
      render: (v: number) => v > 0 ? <Tag>B{v}</Tag> : null,
    },
    { title: "Qty", dataIndex: "quantity", key: "quantity", width: 70 },
    {
      title: "Received",
      dataIndex: "received_date",
      key: "received_date",
      render: (v: string) => v ? formatDate(v) : "-",
    },
    { title: "Lead Time", dataIndex: "lead_time", key: "lead_time", render: (v: string) => <Tag>{v}</Tag> },
    {
      title: "Timeliness",
      dataIndex: "timeliness",
      key: "timeliness",
      render: (v: string) => <Tag color={TIMELINESS_COLORS[v]}>{v?.replace(/_/g, " ").toUpperCase()}</Tag>,
    },
    {
      title: "Due Date",
      dataIndex: "required_completion_date",
      key: "required_completion_date",
      render: (v: string) => v ? formatDate(v) : "-",
    },
    {
      title: "Machine Group",
      dataIndex: "assigned_machine_group_name",
      key: "assigned_machine_group_name",
      render: (v: string) => v || "-",
    },
    {
      title: "Scheduled Start",
      dataIndex: "start_date",
      key: "start_date",
      render: (v: string) => v ? formatShortDateTime(v, "MM/DD HH:mm") : "-",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (v: string) => (
        <Tag color={v === "completed" ? "green" : v === "in_progress" ? "blue" : "default"}>{v}</Tag>
      ),
    },
  ];

  return isLoading ? (
    <Spin className="mt-4 block" />
  ) : (
    <div>
      <div className="mb-3 text-sm text-gray-500">
        Task ID: {taskCode} — {taskName} — {queueCount} total job{queueCount !== 1 ? "s" : ""} in queue
      </div>
      <Table dataSource={items} columns={columns} rowKey="id" size="small" />
    </div>
  );
}

export default function Queues() {
  const { data: queues, isLoading } = useGetQueuesQuery({});
  const [activeKey, setActiveKey] = useState<string>("");

  const queueList = queues || [];

  if (isLoading) return <Spin size="large" className="mt-10 mx-auto block" />;

  const items = queueList.map((q: any) => {
    const hasBadge = q.today_remaining > 0;
    const badgeColor = q.today_has_late ? "red" : "green";

    return {
      key: String(q.task_id),
      label: (
        <Tooltip title={`Task ID: ${q.task_code}`}>
          <span className="inline-flex items-center gap-1.5">
            <TabLabel name={q.task_name} />
            {hasBadge && (
              <span
                className="inline-flex items-center justify-center rounded-full text-white text-xs font-medium min-w-[18px] h-[18px] px-1"
                style={{ backgroundColor: badgeColor === "red" ? "#ff4d4f" : "#52c41a", lineHeight: 1 }}
              >
                {q.today_remaining}
              </span>
            )}
          </span>
        </Tooltip>
      ),
      children: (
        <QueueTab
          taskId={q.task_id}
          taskCode={q.task_code}
          taskName={q.task_name}
          queueCount={q.queue_count}
        />
      ),
    };
  });

  return (
    <div className="w-full p-4">
      <Header title="Queues" />
      <Tabs
        activeKey={activeKey || items[0]?.key}
        onChange={setActiveKey}
        items={items}
        type="card"
        size="small"
      />
    </div>
  );
}
